import { Alert, Box, Chip, CircularProgress, Container, Stack, Typography } from "@mui/material";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";
import { useFindAllOrganizationsQuery } from "@/libs/generated/hooks";
import type { FindAllOrganizationsQuery } from "@/libs/generated/operations";

import { ORGANIZATION_TABLE_COLUMNS } from "./columns";

type OrganizationRecord = FindAllOrganizationsQuery["findAllOrganizations"]["records"][0];

function OrganizationsTable() {
  "use no memo";
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;
  const orderBy = searchParams.get("orderBy") ?? "name";
  const order = searchParams.get("order") === "desc" ? "desc" : "asc";

  const columns = ORGANIZATION_TABLE_COLUMNS;

  const {
    data,
    isPending: loading,
    error,
  } = useFindAllOrganizationsQuery({
    GetAllInput: {
      pagination: {
        page,
        perPage: pageSize,
      },
      sort: {
        field: orderBy,
        order,
      },
      keyword: "",
    },
  });

  const pageCount = Math.ceil((data?.findAllOrganizations?.total ?? 0) / pageSize);

  const handlePaginationChange = (pagination: PaginationState) => {
    const nextParams = new URLSearchParams(searchParams);

    if (pagination.pageIndex > 0) nextParams.set("page", String(pagination.pageIndex));
    else nextParams.delete("page");

    if (pagination.pageSize !== 10) nextParams.set("pageSize", String(pagination.pageSize));
    else nextParams.delete("pageSize");

    setSearchParams(nextParams);
  };

  const handleSortingChange = (sorting: SortingState) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", nextSort.id);
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };

  const { table } = useDataTable<OrganizationRecord>({
    data: data?.findAllOrganizations?.records ?? [],
    columns,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: false,
    externalPagination: { pageIndex: page, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
  });

  if (error !== null && error !== undefined) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading organizations: {error.message}
      </Alert>
    );
  }

  const totalCount = data?.findAllOrganizations?.total ?? 0;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography component="h1" sx={{ textTransform: "capitalize" }} variant="h4">
          {t("sections.organization")}
        </Typography>
        <Chip
          color="primary"
          label={`${totalCount} ${t("common.shared.total")}`}
          variant="outlined"
        />
      </Stack>

      <DataTable table={table} totalCount={totalCount} isLoading={loading}>
        <DataTableToolbar table={table} />
      </DataTable>
    </Container>
  );
}

export default OrganizationsTable;
